import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { useEffect, useState } from 'react'
import { AssetCaseQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Query as TableQuery } from './table_query'
import { Table as AssetTable } from '@/components/asset/table'
import { Form as AddAssetForm } from './add_asset'
import { Form as RemoveAssetForm } from './remove_asset'
import { EditDialog } from './edit_dialog'
import { EditDialog as EditBarcodeDialog } from '../barcode/edit_dialog'

type AssetCase = AssetCaseQuery['assetCases']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openEditBarcode, setOpenEditBarcode] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [assetCase, setAssetCase] = useState<AssetCase | null>(null)

  const { data, loading, error } = useQuery<AssetCaseQuery>(Query, { variables: { filters: { idEq: id } } })

  useEffect(() => {
    if (!data || !id) { return }

    setAssetCase(data.assetCases.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }

  const onEditBarcode = () => {
    setOpenEditBarcode(true)
  }

  const onDelete = () => {
    setOpenDelete(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={() => setOpen(false)}>
        <DialogTitle>
          { loading || !assetCase
            ? <Loading />
            : <>
              <div className="flex flex-row justify-between">
                <div>
                  <span className="flex flex-row gap-4 items-center pb-6">
                    { 'Case ' + assetCase.barcode.barcode }
                  </span>
                </div>
              </div>
              <Divider className="pb-6" />
              { assetCase.warehouse &&
              <div className="pb-6">
                <Button href={`/settings/warehouses/${assetCase.warehouse?.id}`}>
                  View {assetCase.warehouse?.name}
                </Button>
              </div>}
              <div className="grid grid-cols-2 gap-6">
                <AddAssetForm id={assetCase.id} />
                <RemoveAssetForm id={assetCase.id} />
              </div>
            </>}
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { assetCase &&
          <div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-4 items-center align-center" />
              <Divider />
              <AssetTable
                filters={{ assetCaseIdEq: assetCase.id }}
                hiddenColumns={['Warehouse']}
                pageSize={20}
                fixedHeight
              />
              <Divider className="pb-6" />
            </div>
          </div>}
        </DialogBody>
        <DialogActions>
          { assetCase && <Button color="amber" onClick={onEditBarcode}>Edit barcode</Button> }
          { assetCase && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { assetCase && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { assetCase && <EditBarcodeDialog id={assetCase.barcode.id} open={openEditBarcode} setOpen={setOpenEditBarcode} /> }
      { assetCase && <EditDialog id={assetCase.id} open={openEdit} setOpen={setOpenEdit} /> }
      {
        assetCase &&
          <DeleteDialog
            id={assetCase.id}
            mutation={DeleteMutation}
            open={openDelete}
            refetchQueries={[TableQuery]}
            setOpen={setOpenDelete}
            onSuccess={onDeleteSuccess}
          />
      }
    </>
  )
}
